import React, { useRef, useState } from 'react';
import { Form, Input, Select, DatePicker, Button, Modal, message, Tooltip } from 'antd';
import { Plus, Save, Upload, X, Trash2, Paperclip } from 'lucide-react';
import dayjs from 'dayjs';
import { CRITERIA_PRESETS, CRITERION_STATUS, DEPARTMENTS, careerApi, uid, type Criterion, type Roadmap } from '../careerStore';

interface FormValues {
  name: string;
  employee: string;
  department: string;
  previous: string[];
  current: string;
  target?: string;
  future: string[];
  targetMonth?: dayjs.Dayjs | null;
  criteria: Criterion[];
  note?: string;
}

const toValues = (r?: Roadmap | null): FormValues => (r ? {
  name: r.name, employee: r.employee, department: r.department,
  previous: r.steps.slice(0, r.currentIndex), current: r.steps[r.currentIndex], target: r.steps[r.currentIndex + 1],
  future: r.steps.slice(r.currentIndex + 2), targetMonth: r.targetMonth ? dayjs(`${r.targetMonth}-01`) : null,
  criteria: r.criteria, note: r.note,
} : {
  name: '', employee: '', department: '', previous: [], current: '', target: '', future: [], targetMonth: null,
  criteria: CRITERIA_PRESETS.slice(0, 2).map(p => ({ id: uid(), title: p.title, required: p.required, current: '', status: 'todo' as const })),
  note: '',
});

/** Thêm mới / sửa lộ trình thăng tiến */
export default function AddForm({ editing, onClose, onSaved }: { editing?: Roadmap | null; onClose: () => void; onSaved: (id: string) => void }) {
  const [form] = Form.useForm<FormValues>();
  const [attachments, setAttachments] = useState<string[]>(editing?.attachments || []);
  const fileRef = useRef<HTMLInputElement>(null);
  const isCompletedRoadmap = !!editing && editing.currentIndex >= editing.steps.length - 1;

  const cancel = () => {
    if (form.isFieldsTouched()) {
      Modal.confirm({ title: 'Bỏ các thay đổi?', content: 'Thông tin bạn vừa nhập sẽ không được lưu.', okText: 'Bỏ', okButtonProps: { danger: true }, cancelText: 'Tiếp tục nhập', onOk: onClose });
    } else onClose();
  };

  const save = (v: FormValues) => {
    const clean = (a: string[] = []) => a.map(s => s.trim()).filter(Boolean);
    const steps = [...clean(v.previous), v.current.trim(), ...(v.target?.trim() ? [v.target.trim()] : []), ...clean(v.future)];
    const input = {
      name: v.name.trim(), employee: v.employee.trim(), department: v.department,
      steps, currentIndex: clean(v.previous).length,
      targetMonth: v.targetMonth ? v.targetMonth.format('YYYY-MM') : undefined,
      criteria: v.criteria.map(c => ({ ...c, id: c.id || uid(), title: c.title.trim(), required: (c.required || '').trim(), current: (c.current || '').trim() })),
      note: v.note?.trim() || undefined,
      attachments,
    };
    try {
      if (editing) { careerApi.update(editing.id, input); message.success('Đã cập nhật lộ trình'); onSaved(editing.id); }
      else { const r = careerApi.create(input); message.success('Đã tạo lộ trình thăng tiến'); onSaved(r.id); }
    } catch (e: any) { message.error(e?.message || 'Không lưu được lộ trình'); }
  };

  const section = 'bg-white dark:bg-gray-900 p-4 sm:p-5 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm';
  const sectionTitle = 'text-sm font-semibold text-gray-700 dark:text-gray-200 mb-4 pb-2 border-b border-gray-100 dark:border-gray-800';

  return (
    <div className="flex flex-col bg-gray-50 dark:bg-gray-950 min-h-full">
      <div className="bg-white dark:bg-gray-900 px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center shrink-0">
        <h2 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-gray-100">{editing ? `Sửa lộ trình: ${editing.name}` : 'Thêm mới lộ trình thăng tiến'}</h2>
        <button onClick={cancel} aria-label="Đóng" className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1 cursor-pointer"><X size={20} /></button>
      </div>

      <Form form={form} layout="vertical" initialValues={toValues(editing)} onFinish={save} requiredMark scrollToFirstError className="p-4 sm:p-6 space-y-5">
        <div className={section}>
          <h3 className={sectionTitle}>Thông tin chung</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            <Form.Item name="name" label="Tên lộ trình" rules={[{ required: true, whitespace: true, message: 'Nhập tên lộ trình' }]}>
              <Input placeholder="VD: KTV Trưởng lên Quản lý Spa" maxLength={120} />
            </Form.Item>
            <Form.Item name="employee" label="Nhân sự áp dụng" rules={[{ required: true, whitespace: true, message: 'Nhập tên nhân sự' }]}>
              <Input placeholder="Họ tên nhân sự" maxLength={80} />
            </Form.Item>
            <Form.Item name="department" label="Phòng ban" rules={[{ required: true, message: 'Chọn phòng ban' }]}>
              <Select placeholder="-- Chọn phòng ban --" options={DEPARTMENTS.map(d => ({ value: d, label: d }))} showSearch />
            </Form.Item>
            <Form.Item name="targetMonth" label="Hạn đạt mục tiêu">
              <DatePicker picker="month" format="MM/YYYY" className="w-full" placeholder="Chọn tháng" disabledDate={d => d.isBefore(dayjs(), 'month')} />
            </Form.Item>
          </div>
        </div>

        <div className={section}>
          <h3 className={sectionTitle}>Các bậc thăng tiến</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            <Form.Item name="current" label="Vị trí hiện tại" rules={[{ required: true, whitespace: true, message: 'Nhập vị trí hiện tại' }]}>
              <Input placeholder="VD: KTV Trưởng" maxLength={60} />
            </Form.Item>
            <Form.Item
              name="target" label="Vị trí mục tiêu (thăng tiến lên)" dependencies={['current']}
              rules={[
                { required: !isCompletedRoadmap, whitespace: true, message: 'Nhập vị trí mục tiêu' },
                ({ getFieldValue }) => ({
                  validator: (_, v) => (v && v.trim() && v.trim().toLowerCase() === String(getFieldValue('current') || '').trim().toLowerCase()
                    ? Promise.reject(new Error('Vị trí mục tiêu phải khác vị trí hiện tại')) : Promise.resolve()),
                }),
              ]}
            >
              <Input placeholder="VD: Quản lý Spa" maxLength={60} />
            </Form.Item>
            <Form.Item name="previous" label="Các vị trí đã trải qua" tooltip="Gõ tên rồi Enter, theo thứ tự từ cũ đến mới">
              <Select mode="tags" placeholder="VD: Kỹ thuật viên" open={false} suffixIcon={null} tokenSeparators={[',']} />
            </Form.Item>
            <Form.Item name="future" label="Các bậc tiếp theo sau mục tiêu" tooltip="Gõ tên rồi Enter">
              <Select mode="tags" placeholder="VD: Quản lý Chi nhánh" open={false} suffixIcon={null} tokenSeparators={[',']} />
            </Form.Item>
          </div>
        </div>

        <div className={section}>
          <Form.List
            name="criteria"
            rules={[{ validator: (_, v) => (v && v.length ? Promise.resolve() : Promise.reject(new Error('Cần ít nhất 1 điều kiện thăng tiến'))) }]}
          >
            {(fields, { add, remove }, { errors }) => {
              const existing: string[] = (form.getFieldValue('criteria') || []).map((c: Criterion) => c?.title);
              return (
                <>
                  <div className="flex flex-wrap justify-between items-center gap-2 mb-4 pb-2 border-b border-gray-100 dark:border-gray-800">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Điều kiện thăng tiến ({fields.length})</h3>
                    <Button size="small" icon={<Plus size={14} />} onClick={() => add({ id: uid(), title: '', required: '', current: '', status: 'todo' })}>Thêm điều kiện</Button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    <span className="text-xs text-gray-500 mr-1 self-center">Chọn nhanh:</span>
                    {CRITERIA_PRESETS.filter(p => !existing.includes(p.title)).map(p => (
                      <button key={p.key} type="button" onClick={() => add({ id: uid(), title: p.title, required: p.required, current: '', status: 'todo' })}
                        className="text-xs px-2.5 py-1 rounded-full border border-dashed border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-[#3e91ab] hover:text-[#3e91ab] cursor-pointer">
                        + {p.title}
                      </button>
                    ))}
                  </div>
                  <div className="space-y-3">
                    {fields.map(({ key, name }, i) => (
                      <div key={key} className="bg-gray-50 dark:bg-gray-800/60 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
                        <div className="flex items-start gap-2">
                          <span className="text-xs font-bold text-gray-400 mt-2 w-5">{i + 1}.</span>
                          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-x-3">
                            <Form.Item name={[name, 'title']} rules={[{ required: true, whitespace: true, message: 'Nhập tên điều kiện' }]} className="!mb-2">
                              <Input placeholder="Tên điều kiện" maxLength={120} />
                            </Form.Item>
                            <Form.Item name={[name, 'required']} rules={[{ required: true, whitespace: true, message: 'Nhập mức yêu cầu' }]} className="!mb-2">
                              <Input placeholder="Mức yêu cầu (VD: Tối thiểu 24 tháng)" maxLength={120} />
                            </Form.Item>
                            <Form.Item name={[name, 'current']} className="!mb-0">
                              <Input placeholder="Kết quả hiện tại (không bắt buộc)" maxLength={120} />
                            </Form.Item>
                            <Form.Item name={[name, 'status']} className="!mb-0">
                              <Select options={Object.entries(CRITERION_STATUS).map(([value, s]) => ({ value, label: s.label }))} />
                            </Form.Item>
                          </div>
                          <Tooltip title="Bỏ điều kiện này">
                            <button type="button" onClick={() => remove(name)} className="p-1.5 mt-0.5 text-gray-400 hover:text-red-500 cursor-pointer"><Trash2 size={16} /></button>
                          </Tooltip>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Form.ErrorList errors={errors} />
                </>
              );
            }}
          </Form.List>
        </div>

        <div className={section}>
          <h3 className={sectionTitle}>Ghi chú & tài liệu</h3>
          <Form.Item name="note" label="Mô tả thêm / ghi chú">
            <Input.TextArea rows={3} maxLength={1000} showCount placeholder="VD: Yêu cầu không có phàn nàn nghiêm trọng từ khách hàng trong 1 năm qua" />
          </Form.Item>
          <input ref={fileRef} type="file" multiple hidden accept=".pdf,.doc,.docx,.xls,.xlsx"
            onChange={e => {
              const files = Array.from(e.target.files || []);
              const tooBig = files.filter(f => f.size > 10 * 1024 * 1024);
              if (tooBig.length) message.warning(`Bỏ qua ${tooBig.length} tệp lớn hơn 10MB`);
              setAttachments(a => Array.from(new Set([...a, ...files.filter(f => f.size <= 10 * 1024 * 1024).map(f => f.name)])));
              e.target.value = '';
            }} />
          <button type="button" onClick={() => fileRef.current?.click()}
            className="w-full border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-5 flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-800/60 hover:bg-gray-100 dark:hover:bg-gray-800 transition cursor-pointer">
            <Upload className="text-gray-400 mb-2" size={22} />
            <span className="text-sm text-gray-600 dark:text-gray-300">Bấm để chọn tệp đính kèm</span>
            <span className="text-xs text-gray-400 mt-1">PDF, DOCX, XLSX — tối đa 10MB mỗi tệp (hiện chỉ lưu tên tệp)</span>
          </button>
          {attachments.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {attachments.map(n => (
                <span key={n} className="inline-flex items-center gap-1.5 text-xs bg-gray-100 dark:bg-gray-800 rounded-full pl-2.5 pr-1 py-1">
                  <Paperclip size={12} /> {n}
                  <button type="button" onClick={() => setAttachments(a => a.filter(x => x !== n))} aria-label={`Bỏ ${n}`} className="p-0.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 cursor-pointer"><X size={12} /></button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-1">
          <Button onClick={cancel}>Hủy bỏ</Button>
          <Button type="primary" htmlType="submit" icon={<Save size={16} />} style={{ background: '#3e91ab' }}>{editing ? 'Lưu thay đổi' : 'Lưu lộ trình'}</Button>
        </div>
      </Form>
    </div>
  );
}
